<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import type { Donation } from '../../../../types';
import { applyShelterCurrency, getShelterCurrency } from '@Helpers';
import { useCartSteps } from '../../../../composables';
import { UpdateCartDonationService } from '../../../../services';
import useCartStore from '../../../../store';
import { CART_STEPS } from '../../../../types';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { cartData, loading, stepState } = storeToRefs(useCartStore());
const { currentStep, nextStep, previousStep } = useCartSteps();

const selectedDonation = ref<string | null>(null);
const otherAmount = ref<number | null>(null);

const donations = computed(() => cartData.value?.donations ?? []);
const donationsOptions = computed(() => {
  return (
    donations.value.map(({ _id, amount, description }: Donation) => ({
      label: `${applyShelterCurrency(amount)} - ${description}`,
      value: _id,
    })) || []
  );
});

const nextStepButton = computed(() => ({
  icon: nextStep.value.step ? nextStep.value.step.header.icon : '',
  label: nextStep.value.nextIsFinalStep ? 'common.complete' : 'common.nextStep',
}));

onMounted(() => (currentStep.value = CART_STEPS.DONATION));

watch(selectedDonation, (newValue) => newValue && (otherAmount.value = null));
watch(
  () => cartData.value?.donations_details,
  () => initDonationsModel(),
  { immediate: true }
);

function initDonationsModel() {
  const donationAlreadyAtCart = cartData.value?.donations_details;
  const noDonation = !donationAlreadyAtCart.length;

  if (noDonation) return;

  const { _id, amount } = donationAlreadyAtCart[0];

  if (!_id) {
    otherAmount.value = amount;
    selectedDonation.value = null;
    return;
  }

  otherAmount.value = null;
  selectedDonation.value = _id;
}

function onUpdateDonationRequest() {
  const noDonation = !otherAmount.value && !selectedDonation.value;
  if (noDonation) return emits('onStepUpdate', nextStep.value.step!.name);

  const donation = otherAmount.value
    ? {
        _id: null,
        description: 'Custom Amount',
        amount: otherAmount.value,
      }
    : donations.value.filter(({ _id }: Donation) => selectedDonation.value === _id)[0];

  const PAYLOAD = donation;

  UpdateCartDonationService(PAYLOAD).then((data) => {
    cartData.value = data;
    stepState.value![CART_STEPS.DONATION] = PAYLOAD;
    onNextStep();
  });
}

function onNextStep() {
  emits('onStepUpdate', nextStep.value.step!.name);
  loading.value = false;
}
</script>

<template>
  <div>
    <main class="m-auto max-w-[400px] pt-2">
      <AFieldCheckboxButton
        v-model="selectedDonation"
        centeredList
        :options="donationsOptions"
      />

      <InputGroup class="pt-4">
        <InputGroupAddon class="h-9 text-sm">{{ getShelterCurrency() }}</InputGroupAddon>

        <InputNumber
          v-model="otherAmount"
          class="h-9 w-full text-sm placeholder:text-sm"
          pt:input:root="text-sm placeholder:text-sm"
          mode="decimal"
          :maxFractionDigits="2"
          :minFractionDigits="2"
          :placeholder="$t('common.otherAmount')"
          @focus="selectedDonation = null"
          @input="(event) => (otherAmount = Number(event.value))"
        />
      </InputGroup>
    </main>

    <footer class="flex justify-between pt-4">
      <Button
        v-if="previousStep"
        outlined
        class="af-button af-button-success-outlined w-fit"
        icon="pi pi-arrow-left"
        :label="$t('common.back')"
        @click="emits('onStepUpdate', previousStep)"
      />

      <div class="flex w-full justify-end">
        <div class="ml-auto flex items-center gap-2">
          <Button
            class="af-button ml-auto"
            iconPos="right"
            severity="secondary"
            :label="$t('components.forms.button.skip')"
            @click="onNextStep"
          />

          <Button
            class="af-button af-button-success"
            iconPos="right"
            :disabled="!otherAmount && !selectedDonation"
            :label="$t(nextStepButton.label)"
            @click="onUpdateDonationRequest"
          >
            <template #icon>
              <FaIcon
                v-if="nextStepButton.icon"
                class="mr-2"
                :icon="$icons[nextStepButton.icon]"
              />
            </template>
          </Button>
        </div>
      </div>
    </footer>
  </div>
</template>
